import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { ScenarioCard } from '../components/ScenarioCard';
import { PersonaCard } from '../components/PersonaCard';
import {
  useCreateCoachingSessionMutation,
  useRequestCoachingHintMutation,
  useRetryCoachingSessionMutation,
  useEndCoachingSessionMutation,
  useAddCoachingMessageMutation,
  useFinalizeCoachingSessionMutation,
  useGetCoachingResultQuery,
  type ConversationModality,
  type PracticeMode,
  type PracticeInfo,
  type PracticeLanguageView,
  type LevelOutcome,
} from '../store/api/coachingApi';
import { useGetFeaturedScenariosQuery, useGetScenarioQuery, type Scenario } from '../store/api/scenarioApi';
import {
  useGetNpcPersonasQuery,
  useGetNpcLevelsQuery,
  type LevelWithProgress,
  type PersonaWithLevelSummary,
} from '../store/api/npcApi';
import { asArray } from '../store/api/npcResponses';
import {
  useLinkSessionToExamMutation,
  useGetExamDefinitionQuery,
  type DomainKey,
  type ExamProgressResponse,
} from '../store/api/credentialsApi';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { parseCoachingPreset, type CoachingPreset } from '../utils/flowLinks';
import { ExamProgressPanel, LevelOutcomeCard } from '../components/flows/FlowOutcomes';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { streamCoachingMessage, ENGINE_DISABLED } from '../utils/conversationStream';
import { useGetCapabilitiesQuery } from '../store/api/capabilitiesApi';
import { ConversationResults } from '../components/ConversationResults';
import { AddResultToPortfolio } from '../components/portfolio/AddResultToPortfolio';
import { LiveConversationPanel } from '../components/LiveConversationPanel';
import { VideoPracticePanel } from '../components/video/VideoPracticePanel';
import { PracticeLanguagePicker } from '../components/PracticeLanguagePicker';
import { BASE_LANGUAGE, isTargetLanguage, practiceLanguageOption } from '../config/practiceLanguages';
import type { RootState } from '../store';
import { LoadErrorNotice } from '../components/LoadErrorNotice';
import { attemptErrorView } from '../utils/attemptErrors';
import { retryInputMode } from '../utils/attemptRetry';
import { coachingStartErrorMessage, isInsufficientCreditsError } from '../utils/coachingStartError';
import { AddCreditsAction } from '../components/billing/AddCreditsAction';
import { useSpokenAttemptFinalizer } from '../realtime/spokenAttemptFinalize';
import type { InterviewPrepRequest } from '../store/api/interviewPrepApi';
import type { LanguageLearningRequest } from '../store/api/languageLearningApi';
import type { SongPracticeRequest } from '../store/api/songLearningApi';

/**
 * 'text' is Written Chat; 'live' is Live Conversation, the certified realtime
 * runtime, gated on the SERVER's capability. There is no third, pseudo-live
 * value: the legacy WebM-chunk voice capture was removed, so nothing can mount
 * it. 'video' (P5) is Live Conversation with the learner on camera: the same
 * realtime runtime, created as `realtime_audio_video`, gated on the server's
 * `conversation.video` capability.
 */
type InputMode = 'text' | 'live' | 'video';

const VIDEO_LANGUAGE_ASSESSMENT_NOTE =
  'Language assessment is available for Written Chat and Live Conversation, not Video. Turn it off to practise on camera.';
// The interview practice rubric judges written and spoken answers only
// (modalities text and realtime_audio). Offering Video would promise an
// assessment no rubric makes, so it is withheld, with the reason.
const VIDEO_INTERVIEW_PREP_NOTE =
  'Interview Prep is available in Written Chat and Live Conversation, not Video: its answer rubric does not cover video.';
// A graded attempt is held only to a rubric its author named for the modality
// (explicit applicability). No authored rubric judges video evidence today, so
// a graded Video attempt is withheld — practice on camera stays available.
const VIDEO_TESTING_UNAVAILABLE_NOTE =
  'A graded Video attempt is not available for this conversation: no authored rubric judges video evidence. Practise on camera, or take the test in Written Chat or Live Conversation.';
const LIVE_TESTING_UNAVAILABLE_NOTE =
  'A graded Live Conversation is not available for this conversation: its author named no rubric for spoken attempts. Practise live, or take the test in another mode.';
// Development too: a conversation whose author (or flow) named a rubric is
// assessed only on a rubric that covers the chosen modality — never silently
// on the general rubric in its place. The server refuses it; say so first.
const DEVELOPMENT_UNAVAILABLE_NOTE: Record<'text' | 'live' | 'video', string> = {
  text: 'Written Chat is not available for this conversation: no rubric authored for it covers written attempts, and it is not assessed on a general rubric instead.',
  live: 'Live Conversation is not available for this conversation: no rubric authored for it covers spoken attempts, and it is not assessed on a general rubric instead.',
  video: 'Video is not available for this conversation: no rubric authored for it covers video, and it is not assessed on a general rubric instead.',
};
const TEXT_TESTING_UNAVAILABLE_NOTE =
  'A graded Written Chat is not available for this conversation: its author named no rubric for written attempts. Practise in writing, or take the test in another mode.';
/** Engine off (the legacy coaching runtime): it binds no NPC level and grades nothing. */
export const LEVEL_CONVERSATION_UNAVAILABLE_NOTE =
  'Level attempts in a conversation are not available right now. You can attempt NPC levels with Record & Review on the Practice page.';
export const GRADED_CONVERSATION_UNAVAILABLE_NOTE =
  'Graded conversations are not available right now, so this attempt cannot be started.';
/** What ending a legacy (engine-off) conversation honestly produces. */
export const LEGACY_END_NOTE =
  'Your conversation has ended. Conversations here are not scored, so there is no assessment or level result for it.';
/** The server modality each input mode creates its attempt in. */
const INPUT_MODALITY: Record<'text' | 'live' | 'video', string> = {
  text: 'text',
  live: 'realtime_audio',
  video: 'realtime_audio_video',
};
// A speak-along is assessed on the language-practice rubric, which covers
// written and voice attempts only.
const VIDEO_SONG_PRACTICE_NOTE =
  'Song practice is available in Written Chat and Live Conversation, not Video.';

// Display types for persona/scenario cards
export interface DisplayPersona {
  id: string;
  name: string;
  role: string;
  description: string;
  personality: string[];
  difficulty: 'easy' | 'medium' | 'hard';
}

export interface DisplayScenario {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  duration: string;
  /**
   * The modalities a graded attempt may be taken in, as the server states
   * them from the scenario's authored rubric applicability. Absent when the
   * source did not say.
   */
  testingModalities?: string[];
  /** The modalities a Development attempt may be taken in, as the server states them. */
  developmentModalities?: string[];
}

// Map scenario type to display category
function typeToCategory(type: string): string {
  const map: Record<string, string> = {
    pitch: 'Pitch',
    sales: 'Sales',
    negotiation: 'Negotiation',
    interview: 'Interview',
    presentation: 'Presentation',
    networking: 'Networking',
    storytelling: 'Storytelling',
    objection_handling: 'Sales',
    public_speaking: 'Presentation',
    custom: 'Custom',
  };
  return map[type] ?? type;
}

function mapApiScenario(s: Scenario): DisplayScenario {
  // Map 'expert' → 'advanced' for UI display (ScenarioCard only supports 3 tiers)
  const difficulty: DisplayScenario['difficulty'] =
    s.difficulty === 'expert' ? 'advanced' : s.difficulty;
  return {
    id: s.id,
    title: s.title,
    description: s.description,
    category: typeToCategory(s.type),
    difficulty,
    duration: `${s.estimatedMinutes} min`,
    ...(Array.isArray(s.testingModalities) ? { testingModalities: s.testingModalities } : {}),
    ...(Array.isArray(s.developmentModalities) ? { developmentModalities: s.developmentModalities } : {}),
  };
}

// Static persona data (coaching styles are a UI concept, not DB entities yet)
const PERSONAS: DisplayPersona[] = [
  {
    id: 'supportive',
    name: 'Supportive Coach',
    role: 'Encouraging Mentor',
    description: 'Warm and supportive coaching style that builds confidence through positive reinforcement.',
    personality: ['Supportive', 'Patient', 'Motivating'],
    difficulty: 'easy',
  },
  {
    id: 'investor',
    name: 'Skeptical Investor',
    role: 'VC Partner',
    description: 'Analytical and challenging. Asks tough questions about your business model and projections.',
    personality: ['Analytical', 'Direct', 'Skeptical'],
    difficulty: 'hard',
  },
  {
    id: 'prospect',
    name: 'Sales Prospect',
    role: 'Professional Buyer',
    description: 'Experienced buyer who needs convincing. Tests your ability to handle objections.',
    personality: ['Professional', 'Cautious', 'Detail-oriented'],
    difficulty: 'medium',
  },
  {
    id: 'storytelling',
    name: 'Storytelling Coach',
    role: 'Narrative Expert',
    description: 'Focuses on compelling narratives and emotional connection in your delivery.',
    personality: ['Creative', 'Expressive', 'Insightful'],
    difficulty: 'medium',
  },
];


type PageView = 'selection' | 'session';

/** A display persona for a key that is not one of the four coaching styles. */
function personaFromKey(key: string, label?: string, description?: string): DisplayPersona {
  const known = PERSONAS.find((p) => p.id === key);
  if (known) return known;
  return {
    id: key,
    name: label ?? key.replace(/[_-]+/g, ' ').replace(/^./, (c) => c.toUpperCase()),
    role: 'Conversation partner',
    description: description ?? '',
    personality: [],
    difficulty: 'medium',
  };
}

export function CoachingPage() {
  const [searchParams] = useSearchParams();
  const preset = useMemo(() => parseCoachingPreset(searchParams), [searchParams]);
  if (preset.kind !== 'none') {
    // Remount on a different link so no state leaks between presets.
    return <PresetCoaching key={searchParams.toString()} preset={preset} />;
  }
  return <CoachingSelection />;
}

/**
 * CoachingPage opened from a deep link: an exam's graded scenario
 * (`?scenarioId=&mode=testing&examDomain=`) or an NPC ladder level
 * (`?persona=&npcLevelId=`). Only ids and choices come from the URL; the
 * server resolves the content and refuses what the caller may not use.
 */
function PresetCoaching({ preset }: { preset: Exclude<CoachingPreset, { kind: 'none' }> }) {
  const navigate = useNavigate();
  const isScenario = preset.kind === 'scenario';
  const isLevel = preset.kind === 'level';

  const { data: scenarioRow, isLoading: scenarioLoading, isError: scenarioMissing } = useGetScenarioQuery(
    isScenario ? preset.scenarioId : '',
    { skip: !isScenario }
  );

  const { data: npcPersonasData, isLoading: personasLoading } = useGetNpcPersonasQuery(undefined, { skip: !isLevel });
  const npcPersonas = asArray<PersonaWithLevelSummary>(npcPersonasData);
  const npcPersona = isLevel ? npcPersonas.find((p) => p.key === preset.personaKey) : undefined;
  const { data: npcLevelsData, isLoading: levelsLoading } = useGetNpcLevelsQuery(npcPersona?.id ?? '', {
    skip: !npcPersona,
  });
  const npcLevels = asArray<LevelWithProgress>(npcLevelsData);
  const npcLevel = isLevel ? npcLevels.find((l) => l.id === preset.npcLevelId) : undefined;

  const backTo = isLevel ? '/npc/progress' : preset.examDomain ? `/certifications/${preset.examDomain}` : '/coaching';
  const leave = () => navigate(backTo);

  // A level attempt or a graded attempt needs the canonical conversation
  // engine: the legacy runtime binds no level and grades nothing (the server
  // refuses both). Asked of the server, never assumed; unreadable is "not
  // available" rather than a start that can only end in a failed assessment.
  const { data: capabilities, isSuccess: capabilitiesLoaded, isError: capabilitiesFailed } = useGetCapabilitiesQuery();
  const needsEngine = isLevel || (isScenario && preset.mode === 'testing');

  const loading = (isScenario ? scenarioLoading : personasLoading || (!!npcPersona && levelsLoading))
    || (needsEngine && !capabilitiesLoaded && !capabilitiesFailed);
  if (loading) {
    return <p className="py-12 text-center text-gray-500">Loading…</p>;
  }
  const engineUnavailable = needsEngine && capabilities?.conversation?.engine !== true;

  if (isScenario) {
    if (!scenarioRow || scenarioMissing) {
      return <PresetUnavailable message="This scenario is not available to you." backTo={backTo} />;
    }
    if (engineUnavailable) {
      return <PresetUnavailable message={GRADED_CONVERSATION_UNAVAILABLE_NOTE} backTo={backTo} />;
    }
    return (
      <CoachingSessionView
        persona={personaFromKey(scenarioRow.persona)}
        scenario={mapApiScenario(scenarioRow)}
        initialMode={preset.mode}
        lockMode={!!preset.examDomain}
        examDomain={preset.examDomain}
        onEnd={leave}
      />
    );
  }

  if (!npcPersona || !npcLevel) {
    return <PresetUnavailable message="This level is not available." backTo={backTo} />;
  }
  // A locked level offers no Start (the server would refuse it with 422 anyway).
  if (npcLevel.isLocked) {
    return (
      <PresetUnavailable
        message={`${npcLevel.displayName} is locked until the previous level is passed.`}
        backTo={backTo}
      />
    );
  }
  if (engineUnavailable) {
    return <PresetUnavailable message={LEVEL_CONVERSATION_UNAVAILABLE_NOTE} backTo={backTo} />;
  }
  return (
    <CoachingSessionView
      persona={personaFromKey(npcPersona.key, npcPersona.label, npcPersona.description)}
      level={{ id: npcLevel.id, displayName: npcLevel.displayName, minOverallScore: npcLevel.minOverallScore }}
      initialMode="development"
      lockMode
      onEnd={leave}
    />
  );
}

function PresetUnavailable({ message, backTo }: { message: string; backTo: string }) {
  return (
    <div className="py-12 text-center" data-testid="preset-unavailable">
      <p className="text-gray-900 font-medium">{message}</p>
      <Link to={backTo} className="mt-3 inline-block text-sm font-medium text-primary-600 hover:text-primary-700">
        Go back
      </Link>
    </div>
  );
}

function CoachingSelection() {
  const [selectedPersonaId, setSelectedPersonaId] = useState<string | null>(null);
  const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(null);
  const [pageView, setPageView] = useState<PageView>('selection');
  const { data: selectionCapabilities } = useGetCapabilitiesQuery();
  const interviewPrepOn = selectionCapabilities?.conversation?.engine === true;

  // Fetch scenarios from API
  const {
    data: apiScenarios,
    isLoading: scenariosLoading,
    isError: scenariosFailed,
    error: scenariosError,
    refetch: refetchScenarios,
  } = useGetFeaturedScenariosQuery();

  // Map API scenarios to display format
  const scenarios = useMemo(() => {
    if (apiScenarios?.length) {
      return apiScenarios.map(mapApiScenario);
    }
    return [];
  }, [apiScenarios]);

  const selectedPersona = PERSONAS.find((p) => p.id === selectedPersonaId);
  const selectedScenario = scenarios.find((s) => s.id === selectedScenarioId);

  const canStartSession = selectedPersona && selectedScenario;

  const handleStartSession = () => {
    if (canStartSession) {
      setPageView('session');
    }
  };

  const handleEndSession = () => {
    setPageView('selection');
    setSelectedPersonaId(null);
    setSelectedScenarioId(null);
  };

  // Show session view
  if (pageView === 'session' && selectedPersona && selectedScenario) {
    return (
      <CoachingSessionView
        persona={selectedPersona}
        scenario={selectedScenario}
        onEnd={handleEndSession}
      />
    );
  }

  // Show selection view
  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">AI Coaching</h1>
        <p className="mt-1 text-gray-500">
          Practice with AI-powered coaches in realistic scenarios
        </p>
        {/* Interview Prep runs on the conversation engine: no link while it is off (or unknown). */}
        {interviewPrepOn && (
          <p className="mt-2 text-sm text-gray-600">
            Preparing for a job interview?{' '}
            <Link to="/interview-prep" className="font-medium text-primary-700 hover:text-primary-900 underline">
              Try Interview Prep
            </Link>
          </p>
        )}
      </div>

      {/* Persona selection */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          1. Choose Your Coach
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {PERSONAS.map((persona) => (
            <PersonaCard
              key={persona.id}
              id={persona.id}
              name={persona.name}
              role={persona.role}
              description={persona.description}
              personality={persona.personality}
              difficulty={persona.difficulty}
              isSelected={selectedPersonaId === persona.id}
              onSelect={setSelectedPersonaId}
            />
          ))}
        </div>
      </section>

      {/* Scenario selection */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          2. Select a Scenario
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {scenariosLoading && (
            <div className="col-span-full text-center py-8 text-gray-500">Loading scenarios...</div>
          )}
          {/* A failed load is not "No scenarios available yet". */}
          {!scenariosLoading && scenariosFailed && (
            <div className="col-span-full py-4">
              <LoadErrorNotice
                error={scenariosError}
                onRetry={() => void refetchScenarios()}
                failedMessage="Scenarios could not be loaded."
                testIdPrefix="coaching-scenarios"
              />
            </div>
          )}
          {!scenariosLoading && !scenariosFailed && scenarios.length === 0 && (
            <div className="col-span-full text-center py-8 text-gray-400">No scenarios available yet.</div>
          )}
          {!scenariosLoading && scenarios.map((scenario) => (
            <ScenarioCard
              key={scenario.id}
              id={scenario.id}
              title={scenario.title}
              description={scenario.description}
              category={scenario.category}
              difficulty={scenario.difficulty}
              duration={scenario.duration}
              isSelected={selectedScenarioId === scenario.id}
              onSelect={setSelectedScenarioId}
            />
          ))}
        </div>
      </section>

      {/* Start session panel */}
      {canStartSession && (
        <div className="card bg-gradient-to-r from-primary-50 to-primary-100 border-primary-200">
          <div className="card-body">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h3 className="font-semibold text-gray-900">Ready to Start</h3>
                <p className="text-sm text-gray-600 mt-1">
                  You'll be coached by <span className="font-medium text-primary-700">{selectedPersona.name}</span> for a{' '}
                  <span className="font-medium text-primary-700">{selectedScenario.title}</span> scenario.
                </p>
              </div>
              <button
                onClick={handleStartSession}
                className="btn-primary px-6 py-3 text-base"
              >
                Start Coaching Session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help text when no selection */}
      {!canStartSession && (
        <div className="text-center py-8 text-gray-400">
          <p>Select a coach and scenario to begin your session.</p>
        </div>
      )}
    </div>
  );
}

// Active Coaching Session Component
export interface CoachingSessionViewProps {
  persona: DisplayPersona;
  /** Absent for an NPC level: that is open coaching with the level's persona. */
  scenario?: DisplayScenario;
  /** An NPC ladder level this conversation attempts. */
  level?: { id: string; displayName: string; minOverallScore: number };
  /** Development or Testing to begin in. */
  initialMode?: PracticeMode;
  /** The mode is fixed by the link (an exam is always graded; a level is not chosen). */
  lockMode?: boolean;
  /** Link the finalized session to this domain's open exam attempt. */
  examDomain?: string | null;
  onEnd: () => void;
  /**
   * Interview Prep (P5). The attempt is started from the learner's format and
   * context; the SERVER chooses the authored interview scenario, so no
   * scenarioId is sent. Testing is offered only when that scenario's author
   * named a rubric.
   */
  interviewPrep?: { request: InterviewPrepRequest; testingAvailable: boolean };
  /**
   * Language Learning (P5). The learner's language profile and the catalog
   * scenario they chose; the server validates both and freezes the profile
   * into the attempt. The language is fixed by the profile. Testing is offered
   * only in the modalities the scenario's authored rubric applies to.
   */
  languageLearning?: {
    request: LanguageLearningRequest; scenarioId: string; testingModalities: string[];
    /** Development modalities (authored or language rubric); absent: not stated. */
    developmentModalities?: string[];
  };
  /**
   * Song learning (P5.5): a speak-along on one section of a rights-cleared
   * piece. The client names the piece and section; the SERVER supplies the
   * words through its rights gate. Always Development practice in the piece's
   * language, assessed on the language-practice rubric.
   */
  songPractice?: { request: SongPracticeRequest; language: string };
}


interface DisplayMessage {
  id: string;
  from: 'user' | 'coach';
  text: string;
  createdAt: Date;
  /** A Development hint: addressed to the learner, not said by the persona. */
  kind?: 'hint';
}

/**
 * The requests that end and evaluate an attempt, in order. A canonical
 * (conversation-engine) session is completed by finalize itself, so it is
 * NOT sent to POST /end — which only knows legacy rows and answered 404 for
 * it: a wasted, rate-limited request. A legacy session (engine off) is ended
 * by /end ONLY: finalize exists only on the conversation engine (it answers
 * 404 with the engine off), so calling it reported a false "assessment could
 * not be completed" for a conversation that is simply never scored.
 */
export function endAttemptRequests(canonical: boolean): Array<'end' | 'finalize'> {
  return canonical ? ['finalize'] : ['end'];
}

/** Authored catalog scenarios have UUID ids; only they can be graded. */
const isAuthoredScenarioId = (id: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

export function CoachingSessionView({
  persona,
  scenario,
  level,
  initialMode = 'development',
  lockMode = false,
  examDomain = null,
  onEnd,
  interviewPrep,
  languageLearning,
  songPractice,
}: CoachingSessionViewProps) {
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [isCoachTyping, setIsCoachTyping] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  // The start was refused for credits: the notice offers Add Credits / "ask your owner".
  const [sessionErrorIsCredits, setSessionErrorIsCredits] = useState(false);
  /** Live reply text. DISPLAY ONLY — replaced by the persisted turn on completion. */
  const [streamingText, setStreamingText] = useState('');
  const [sendError, setSendError] = useState<{ message: string; text: string } | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  /** True while the user has scrolled up; suppresses auto-scroll so we don't fight them. */
  const userPinnedRef = useRef(false);
  /** After finishing, the user sees their assessment rather than being dumped out. */
  const [showResults, setShowResults] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [evaluateError, setEvaluateError] = useState<string | null>(null);
  const [inputMode, setInputMode] = useState<InputMode>('text');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  /**
   * Development (coaching, hints, retries) or Testing (a graded attempt). Chosen
   * before the first message; the server freezes it into the attempt, so it
   * cannot change mid-conversation.
   */
  const [practiceMode, setPracticeMode] = useState<PracticeMode>(initialMode);
  const [practiceInfo, setPracticeInfo] = useState<PracticeInfo | null>(null);
  /**
   * The conversation language (P5), chosen before the attempt like the mode,
   * for Written Chat and Live Conversation alike. Defaults to the base
   * language; the server freezes it into the attempt.
   */
  const [practiceLanguage, setPracticeLanguage] = useState<string>(songPractice?.language ?? languageLearning?.request.language ?? BASE_LANGUAGE);
  /** Development only: opt in to the language-practice rubric. */
  const [languageAssessment, setLanguageAssessment] = useState(!!songPractice);
  // The language-practice rubric judges written and voice attempts only; no
  // rubric was widened to video. Rather than let the server refuse the attempt
  // after the learner has consented and turned their camera on, say so here.
  const videoBlockedByLanguageAssessment =
    practiceMode === 'development' && languageAssessment && isTargetLanguage(practiceLanguage);
  // Song practice is never graded: its display scenario id is not an authored
  // (UUID) id, so canTest is false, and the server refuses Testing for it too.
  /**
   * Where a graded attempt can be assessed, per the server (the scenario's
   * authored rubric applicability). null: not stated, so no per-modality gate.
   */
  const authoredTestingModalities: string[] | null = languageLearning
    ? languageLearning.testingModalities
    : scenario?.testingModalities ?? null;
  // The server says its conversation engine is OFF (an explicit false): the
  // legacy runtime it would use grades nothing, so no graded attempt is offered
  // (the server refuses one too). Unknown is not "off".
  const { data: engineCapabilities, isSuccess: engineCapabilitiesLoaded } = useGetCapabilitiesQuery();
  const engineOff = engineCapabilitiesLoaded && engineCapabilities?.conversation?.engine === false;
  const authoredCanTest = interviewPrep
    ? interviewPrep.testingAvailable
    : authoredTestingModalities ? authoredTestingModalities.length > 0
    : !!scenario && isAuthoredScenarioId(scenario.id);
  const canTest = !engineOff && authoredCanTest;
  /** True when Testing is offered but not in this input mode's modality. */
  const testingUnavailableIn = (mode: InputMode) =>
    canTest && !!authoredTestingModalities && !authoredTestingModalities.includes(INPUT_MODALITY[mode]);
  const gradedAttempt = practiceMode === 'testing' && canTest;
  /** Development: modalities the server says can be assessed (null: not stated). */
  const authoredDevelopmentModalities: string[] | null = languageLearning
    ? languageLearning.developmentModalities ?? null
    : scenario?.developmentModalities ?? null;
  const developmentUnavailableIn = (mode: InputMode) =>
    !gradedAttempt && !!authoredDevelopmentModalities && !authoredDevelopmentModalities.includes(INPUT_MODALITY[mode]);
  /** Why Video is withheld for this attempt, or null when it may be offered. */
  const videoTestingUnavailable = gradedAttempt && testingUnavailableIn('video');
  const liveBlockedReason: string | null = gradedAttempt && testingUnavailableIn('live') ? LIVE_TESTING_UNAVAILABLE_NOTE
    : developmentUnavailableIn('live') ? DEVELOPMENT_UNAVAILABLE_NOTE.live : null;
  const textBlockedReason: string | null = sessionId ? null
    : gradedAttempt && testingUnavailableIn('text') ? TEXT_TESTING_UNAVAILABLE_NOTE
    : developmentUnavailableIn('text') ? DEVELOPMENT_UNAVAILABLE_NOTE.text : null;
  const videoBlockedReason: string | null = interviewPrep
    ? VIDEO_INTERVIEW_PREP_NOTE
    : songPractice
    ? VIDEO_SONG_PRACTICE_NOTE
    : videoTestingUnavailable ? VIDEO_TESTING_UNAVAILABLE_NOTE
    : developmentUnavailableIn('video') ? DEVELOPMENT_UNAVAILABLE_NOTE.video
    : videoBlockedByLanguageAssessment ? VIDEO_LANGUAGE_ASSESSMENT_NOTE : null;
  /** The language the current attempt was bound to, as the server states it. */
  const [sessionLanguage, setSessionLanguage] = useState<PracticeLanguageView | null>(null);
  const [hintBusy, setHintBusy] = useState(false);
  const [hintError, setHintError] = useState<string | null>(null);
  const [requestHint] = useRequestCoachingHintMutation();
  const [retrySession] = useRetryCoachingSessionMutation();
  /** The NPC level outcome reported by finalize (passed / not yet / not evaluable). */
  const [levelOutcome, setLevelOutcome] = useState<LevelOutcome | null>(null);
  /** Exam linking after finalize: progress on success, or why it did not count. */
  const [examLink, setExamLink] = useState<
    { state: 'linked'; progress: ExamProgressResponse | null } | { state: 'refused'; message: string } | null
  >(null);
  const [linkSessionToExam] = useLinkSessionToExamMutation();
  const { data: examDefinition } = useGetExamDefinitionQuery((examDomain ?? '') as DomainKey, { skip: !examDomain });

  // R0.1: the 'ai'/'voice' plan-capability read lived here only to render a
  // "(Pro AI)" badge on the voice toggle. That badge advertised a purchasable
  // realtime feature that is not currently exercisable, so it was removed along
  // with the read. R1C reinstates both when the capability is real.

  // RTK Query mutations
  const accessToken = useSelector((st: RootState) => st.centralAuth?.accessToken);

  const [createSession] = useCreateCoachingSessionMutation();
  const [endSessionMutation] = useEndCoachingSessionMutation();
  const [addMessage] = useAddCoachingMessageMutation();

  // ── TRANSPORT IS CHOSEN BEFORE ANYTHING IS SENT ────────────────────────────
  //
  // The server states whether it has a canonical streaming runtime; the UI does
  // not discover it by POSTing to the stream endpoint and reading a 404. Probing
  // would make the ordinary engine-off configuration produce a failed request on
  // every first message, and would make a genuine outage look identical to a
  // setting.
  //
  // `streaming` and the non-streaming endpoint are not two different
  // conversations. With the engine on, both run the SAME canonical runtime and
  // persist the same turns — streaming only delivers the reply incrementally.
  // That is why falling back to it is safe, and why no partial text is faked
  // when it is used.
  const {
    data: capabilities,
    isSuccess: capabilitiesLoaded,
    isError: capabilitiesUnavailable,
    refetch: refetchCapabilities,
  } = useGetCapabilitiesQuery();

  // Unreadable capabilities resolve to the non-streaming endpoint: it is the one
  // path present on every deployment, including a server predating this route.
  // Live Conversation requires BOTH capabilities. The server already reports
  // the conjunction, so the UI never has to combine flags itself — and cannot
  // get that combination wrong.
  const realtimeVoiceAvailable = capabilitiesLoaded
    ? capabilities.conversation.realtimeVoice === true
    : false;
  // Video practice: the server reports engine AND voice AND video together.
  const videoAvailable = capabilitiesLoaded
    ? capabilities.conversation.video === true
    : false;

  const transport: 'streaming' | 'legacy' | 'resolving' = capabilitiesLoaded
    ? capabilities.conversation.textStreaming
      ? 'streaming'
      : 'legacy'
    : capabilitiesUnavailable
      ? 'legacy'
      : 'resolving';
  /**
   * The live conversation was ended, so THIS attempt is finished.
   *
   * Ending finalizes the canonical session (status=completed). Reusing that id
   * is what produced SESSION_UNAVAILABLE when the user pressed Live
   * Conversation again: admission correctly refuses a completed session, and
   * correctly is the right behaviour — the defect was reusing the id.
   *
   * The replacement session is created LAZILY, when the user actually starts
   * talking again. Creating it on End would reserve credits, once metering is
   * on, for a conversation nobody asked for.
   */
  const [attemptEnded, setAttemptEnded] = useState(false);
  /** The modality the current attempt was created in; null before any exists. */
  const [sessionModality, setSessionModality] = useState<ConversationModality | null>(null);
  /** The attempt a "Try again" retries, until the learner commits the retry (handleRetry). */
  const [pendingRetryOf, setPendingRetryOf] = useState<string | null>(null);
  /**
   * Whether the current attempt is a canonical (conversation-engine) session,
   * as the server's capabilities stated when it was created. Finalize ends a
   * canonical session itself; POST /end only knows legacy rows (it answers 404
   * for an open canonical one), so calling it was a wasted, rate-limited
   * request on every End.
   */
  const [sessionCanonical, setSessionCanonical] = useState(false);

  const [finalizeSession] = useFinalizeCoachingSessionMutation();
  /** Said alongside the result (e.g. it was already finalized), or null. */
  const [finalizeNote, setFinalizeNote] = useState<string | null>(null);
  /** The attempt whose finalize was refused retryably ("Try again"), or null. */
  const [retryFinalizeId, setRetryFinalizeId] = useState<string | null>(null);
  /** A legacy (engine-off) conversation was ended: it is not scored, and says so. */
  const [legacyEnded, setLegacyEnded] = useState(false);
  const [legacyEndError, setLegacyEndError] = useState<string | null>(null);
  /**
   * Finalize — once per attempt — and show its result. Shared by End Session
   * (a written attempt) and the end of a spoken one (Live / Video: End pressed
   * or the server completing it), so a spoken attempt is scored without a
   * second, separate End Session press. See realtime/spokenAttemptFinalize.
   */
  const { finalizer } = useSpokenAttemptFinalizer(
    (id) => finalizeSession(id).unwrap(),
    (state) => {
      if (state.phase === 'finalizing') {
        setEvaluating(true);
        setEvaluateError(null);
        setFinalizeNote(null);
        setRetryFinalizeId(null);
        return;
      }
      setEvaluating(false);
      if (state.phase === 'finalized') {
        setLevelOutcome(state.level);
        setFinalizeNote(state.note);
        setShowResults(true);
        // linkToExam reports its own outcome; an exam-link problem is not an
        // assessment failure.
        if (examDomain) void linkToExam(state.sessionId);
        return;
      }
      // No score is invented when evaluation fails. A refusal with a known code
      // (in progress, discarded, usage/Central unavailable) is told in the
      // server's terms from the one map.
      setEvaluateError(state.error.message);
      setRetryFinalizeId(state.error.retryable ? state.sessionId : null);
      setShowResults(true);
    }
  );
  // Un-skipped by showResults: setting it (after finalize) is what fetches the
  // result. Never call refetch() from the same tick — the query has not started
  // yet, RTK Query throws, and a finished assessment was reported as failed.
  const {
    data: result,
    isError: resultFailed,
    error: resultError,
    refetch: refetchResult,
  } = useGetCoachingResultQuery(sessionId!, {
    skip: !sessionId || !showResults,
  });

  /**
   * Create the canonical attempt, in the modality the user just committed to.
   *
   * ── LAZY, PER MODE ─────────────────────────────────────────────────────────
   *
   * A session's modality is frozen into its binding, and the binding decides
   * what the persona is told — written or spoken. Creating the session when the
   * coaching view opened, before the user had chosen Written or Live, bound
   * every attempt as written, so Live Conversation told the persona "you cannot
   * hear the other person" while the user spoke to it.
   *
   * So nothing is created until the user COMMITS: the first written Send, or
   * Start Live Conversation. Opening the view and switching tabs are UI state
   * only — no session, no reservation, no microphone, no socket, and no
   * zero-turn session left behind by someone exploring the page.
   */
  const createAttempt = useCallback(async (modality: ConversationModality): Promise<string | null> => {
    setSessionError(null);
    setSessionErrorIsCredits(false);
    if (pendingRetryOf) {
      // A committed "Try again": the retry is created NOW, in the mode the
      // learner chose, under the server's retry rules (handleRetry).
      try {
        const next = await retrySession({ id: pendingRetryOf, modality }).unwrap();
        setPendingRetryOf(null);
        setSessionId(next.id);
        // A retry exists only on the conversation engine.
        setSessionCanonical(true);
        setPracticeInfo(next.practice ?? null);
        setSessionLanguage(next.language ?? null);
        setSessionModality(modality);
        setMessages([]);
        setAttemptEnded(false);
        return next.id;
      } catch (error) {
        const code = (error as { data?: { error?: { code?: string } } })?.data?.error?.code;
        setSessionError(code === 'RETRY_LIMIT_REACHED' ? 'You have used all the retries for this practice.' : attemptErrorView(error, 'A retry could not be started right now.').message);
        // A refused retry is over: the view returns to a normal start (mode and
        // language pickers back), so the learner is never stuck in retry mode.
        setPendingRetryOf(null);
        return null;
      }
    }
    try {
      // persona.id, not persona.name.
      //
      // `persona` on this request IS the persona key — the controller passes it
      // straight through as `personaKey`. Sending the display name made the
      // engine reject every session with
      //   "Unknown persona 'Skeptical Investor'. Known personas: default,
      //    investor, media, prospect, storytelling, supportive"
      // and the user saw only "Failed to start coaching session." The four
      // PERSONAS ids are exactly those keys.
      //
      // scenarioId, so the authored scenario is actually used. Passing only a
      // title left scenarioSource undefined, and the engine bound a default
      // scenario while the UI claimed the one the user picked. The id makes the
      // server read the authored row from the catalog, which is also why the
      // client cannot supply the content itself.
      const mode = practiceMode === 'testing' && canTest ? 'testing' : 'development';
      const session = await createSession({
        persona: persona.id,
        // Interview Prep names a format and the learner's context; the server
        // resolves the authored interview scenario from it.
        // Song practice names a piece and section; the server reads the words
        // through its rights gate and fixes language, mode and rubric.
        ...(songPractice
          ? { songPractice: songPractice.request }
          : interviewPrep
          ? { interviewPrep: interviewPrep.request }
          : languageLearning
            // The learner's profile and the catalog scenario they chose; the
            // server validates both and resolves the authored content.
            ? { languageLearning: languageLearning.request, scenarioId: languageLearning.scenarioId }
            : scenario ? { scenarioId: scenario.id, scenarioName: scenario.title } : {}),
        // An NPC level: the server checks it belongs to this persona and is unlocked.
        ...(level ? { npcLevelId: level.id } : {}),
        modality,
        mode,
        language: languageLearning ? languageLearning.request.language : practiceLanguage,
        // An explicit opt-in, offered only in Development for a target language.
        // Language Learning scenarios author their own (language) rubric.
        ...(!languageLearning && mode === 'development' && languageAssessment && isTargetLanguage(practiceLanguage)
          ? { languageAssessment: true }
          : {}),
      }).unwrap();

      setSessionId(session.id);
      setPracticeInfo(session.practice ?? null);
      setSessionLanguage(session.language ?? null);
      setSessionModality(modality);
      setSessionCanonical(capabilities?.conversation?.engine === true);
      setMessages([]);
      setAttemptEnded(false);
      // Returned, not read back from state: setSessionId is asynchronous, so a
      // caller continuing in the same tick would otherwise use the OLD id — the
      // completed one whose reuse caused SESSION_UNAVAILABLE in the first place.
      return session.id;
    } catch (error) {
      console.error('Failed to create coaching session:', error);

      // Say what actually happened — a plan limit, a safety cap, credits,
      // missing content… — never a generic "try again" for a limit that a
      // retry cannot lift (utils/coachingStartError, RC19).
      setSessionErrorIsCredits(isInsufficientCreditsError(error));
      setSessionError(
        coachingStartErrorMessage(error, {
          languageLearning: Boolean(languageLearning),
          levelUnavailableNote: LEVEL_CONVERSATION_UNAVAILABLE_NOTE,
          gradedUnavailableNote: GRADED_CONVERSATION_UNAVAILABLE_NOTE,
        })
      );
      return null;
    }
  }, [createSession, retrySession, pendingRetryOf, persona.id, scenario, level, practiceMode, canTest, practiceLanguage, languageAssessment, interviewPrep, languageLearning, songPractice, capabilities]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    // Don't yank the view back down if the user has deliberately scrolled up to
    // re-read something while a reply streams in.
    if (userPinnedRef.current) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isCoachTyping, streamingText]);

  /**
   * Replace the optimistic bubble with what the server actually persisted.
   *
   * Shared by both transports on purpose: a streamed reply and a non-streaming
   * response are two ways of learning about the SAME canonical turns, so they
   * must converge on identical history. Ids are de-duplicated because a turn may
   * already be present from an earlier load.
   */
  const reconcile = useCallback(
    (data: {
      userMessage: { id: string; content: string; timestamp?: string };
      coachMessage: { id: string; content: string; timestamp?: string };
    }) => {
      setMessages(prev => {
        const kept = prev.filter(m => !m.id.startsWith('temp-'));
        const seen = new Set(kept.map(m => m.id));
        const next = [...kept];
        if (!seen.has(data.userMessage.id)) {
          next.push({
            id: data.userMessage.id, from: 'user',
            text: data.userMessage.content,
            createdAt: new Date(data.userMessage.timestamp ?? Date.now()),
          });
        }
        if (!seen.has(data.coachMessage.id)) {
          next.push({
            id: data.coachMessage.id, from: 'coach',
            text: data.coachMessage.content,
            createdAt: new Date(data.coachMessage.timestamp ?? Date.now()),
          });
        }
        return next;
      });
      setStreamingText('');
    },
    []
  );

  /**
   * Send over the non-streaming endpoint.
   *
   * Not a degraded conversation — with the engine on this runs the same
   * canonical runtime — only a non-incremental delivery. Nothing is rendered
   * progressively because nothing arrives progressively; the typing indicator
   * stays up until the persisted reply lands.
   */
  const sendViaLegacy = useCallback(
    async (id: string, userText: string, tempId: string) => {
      try {
        const data = await addMessage({ id, content: userText }).unwrap();
        reconcile(data);
      } catch (error) {
        setMessages(prev => prev.filter(m => m.id !== tempId));
        setStreamingText('');
        // A busy turn, an unreachable usage authority or Central is said as
        // such (utils/attemptErrors); only an unknown failure is "unreachable".
        setSendError({
          message: attemptErrorView(error, 'The conversation model could not be reached. Please try again.').message,
          text: userText,
        });
      }
    },
    [addMessage, reconcile]
  );

  /**
   * The written attempt to send into, created on the first Send.
   *
   * An existing attempt is reused only if it is still open AND written. A
   * spoken attempt never takes a typed turn (the server refuses it too):
   * switching modality after an attempt has begun starts a NEW attempt, and the
   * old one keeps its own binding, turns and evidence untouched.
   */
  const ensureWrittenAttempt = useCallback(async (): Promise<string | null> => {
    if (sessionId && !attemptEnded && sessionModality === 'text') return sessionId;
    return createAttempt('text');
  }, [sessionId, attemptEnded, sessionModality, createAttempt]);

  const handleSend = useCallback(async () => {
    if (!inputValue.trim()) return;
    // No transport has been chosen yet. Sending now would have to guess.
    if (transport === 'resolving') return;

    const userText = inputValue.trim();
    setInputValue('');
    setSendError(null);

    // The previous attempt was ended, which finalized its canonical session.
    // Typing is a new conversation and needs a live session — reaching a
    // completed one would fail on the first message.
    const activeSessionId = await ensureWrittenAttempt();
    if (!activeSessionId) return;

    const tempId = `temp-${Date.now()}`;
    setMessages(prev => [...prev, {
      id: tempId, from: 'user', text: userText, createdAt: new Date(),
    }]);
    setIsCoachTyping(true);
    setStreamingText('');

    try {
      if (transport !== 'streaming') {
        await sendViaLegacy(activeSessionId, userText, tempId);
        return;
      }

      const controller = new AbortController();
      abortRef.current = controller;

      // Set ONLY by the server saying this deployment has no streaming runtime.
      let engineDisabled = false;

      // The reply is rendered as it arrives, but the PERSISTED turns replace it on
      // completion — a dropped delta must never leave the UI disagreeing with what
      // the server recorded. A failed or cancelled stream persisted nothing, so the
      // optimistic bubble is removed and the user's text handed back to them.
      try {
        await streamCoachingMessage(
          activeSessionId,
          userText,
          accessToken,
          {
            onDelta: (delta) => setStreamingText(prev => prev + delta),
            onCompleted: (data) => reconcile(data),
            onFailed: (err) => {
              if (err.code === ENGINE_DISABLED) {
                // A configuration race: the flag changed under a loaded tab.
                // Nothing reached a provider and nothing was persisted, so this
                // send is completed on the other transport instead. The bubble
                // stays and the user is shown no error, because none occurred.
                engineDisabled = true;
                setStreamingText('');
                return;
              }
              setMessages(prev => prev.filter(m => m.id !== tempId));
              setStreamingText('');
              setSendError({ message: err.message, text: err.preservedUserMessage ?? userText });
            },
            onCancelled: (err) => {
              setMessages(prev => prev.filter(m => m.id !== tempId));
              setStreamingText('');
              setSendError({
                message: 'Reply cancelled — your message was not sent.',
                text: err.preservedUserMessage ?? userText,
              });
            },
          },
          controller.signal
        );
      } finally {
        abortRef.current = null;
      }

      // The ONLY condition that re-sends on another transport. A provider
      // failure deliberately does not: that turn may already have run, and
      // silently re-issuing it would double a request the user never repeated.
      if (engineDisabled) {
        refetchCapabilities();
        await sendViaLegacy(activeSessionId, userText, tempId);
      }
    } finally {
      setIsCoachTyping(false);
    }
  }, [inputValue, ensureWrittenAttempt, accessToken, transport, sendViaLegacy, reconcile, refetchCapabilities]);

  const handleCancelReply = useCallback(() => {
    abortRef.current?.abort();
  }, []);


  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Same gate as the button: Enter must not bypass transport selection.
      if (transport === 'resolving' || textBlockedReason !== null) return;
      handleSend();
    }
  };

  /**
   * Finish and evaluate.
   *
   * Evaluation is requested but produced entirely server-side from canonical
   * turns. If it cannot run, the user is told — they are never shown a score that
   * was not measured.
   */
  const handleEndSession = async () => {
    if (!sessionId) { onEnd(); return; }
    // A spoken attempt that already ended is being (or was) finalized from its
    // end; End Session does not ask a second time.
    if (finalizer.requested(sessionId)) return;

    // Exactly one request ends the attempt: finalize for a canonical session
    // (it completes an open one), /end only for a legacy session.
    if (endAttemptRequests(sessionCanonical).includes('end')) {
      try {
        await endSessionMutation(sessionId).unwrap();
        // Legacy conversations are not scored. Say exactly that — never a
        // failed assessment that was never going to run.
        setLegacyEndError(null);
        setLegacyEnded(true);
      } catch (error) {
        setLegacyEndError(attemptErrorView(error, 'Your conversation could not be ended right now. Try again.').message);
      }
      return;
    }

    await finalizer.finish(sessionId);
  };

  /**
   * Count this finished, graded conversation toward the open exam attempt.
   * The server decides whether it qualifies (completed, Testing, known overall,
   * a scenario authored for the exam, started after the attempt) and computes
   * every number; the request names only the session.
   */
  const linkToExam = async (id: string) => {
    if (!examDomain) return;
    try {
      const linked = await linkSessionToExam({ domainKey: examDomain as DomainKey, sessionId: id }).unwrap();
      setExamLink({ state: 'linked', progress: linked.progress ?? null });
    } catch (error) {
      const code = (error as { data?: { error?: { code?: string } } })?.data?.error?.code;
      const status = (error as { status?: number })?.status;
      setExamLink({
        state: 'refused',
        message:
          code === 'EVIDENCE_NOT_AUTHORITATIVE'
            ? 'This conversation does not count toward the exam: only a completed graded attempt on an exam scenario with a measured overall score counts.'
            : status === 409
              ? 'This conversation has already been counted toward an exam.'
              : status === 404
                ? 'There is no open exam attempt for this certification. Start the exam from Certifications.'
                : 'This conversation could not be linked to the exam right now.',
      });
    }
  };

  /** Development: ask for a hint. The server decides whether one is allowed. */
  const handleHint = async () => {
    if (!sessionId || hintBusy) return;
    setHintBusy(true);
    setHintError(null);
    try {
      const hint = await requestHint(sessionId).unwrap();
      setMessages((prev) => [...prev, { id: `hint-${hint.hintsUsed}-${sessionId}`, from: 'coach', kind: 'hint', text: hint.text, createdAt: new Date() }]);
      setPracticeInfo((p) => (p && p.hints ? { ...p, hints: { used: hint.hintsUsed, allowed: hint.hintsAllowed } } : p));
    } catch (error) {
      const code = (error as { data?: { error?: { code?: string } } })?.data?.error?.code;
      setHintError(
        code === 'HINT_LIMIT_REACHED' ? 'You have used all the hints for this practice.'
          : code === 'NOT_PERMITTED_IN_TESTING' ? 'Hints are not available in a graded attempt.'
          : attemptErrorView(error, 'A hint could not be given right now. Please try again.').message
      );
    } finally {
      setHintBusy(false);
    }
  };

  /**
   * Development "Try again": UI ONLY, for every mode. Nothing is created here.
   * The view returns to the attempt's own mode (Written / Live / Video) and
   * remembers what it retries; the FIRST commitment (the first Send, or Start
   * of Live / Video) creates the retry through POST /:id/retry in the mode
   * the learner chose, so the server's retry rules (limit, one retry per
   * attempt, the retryOf chain, frozen content) apply and a Try again the
   * learner abandons creates nothing: one session per committed attempt.
   */
  const handleRetry = () => {
    if (!sessionId) return;
    setPendingRetryOf(sessionId);
    setSessionId(null);
    setMessages([]);
    setAttemptEnded(false);
    setShowResults(false);
    setEvaluateError(null);
    setFinalizeNote(null);
    setRetryFinalizeId(null);
    setSessionError(null);
    setSessionErrorIsCredits(false);
    setInputMode(retryInputMode(sessionModality));
  };

  const hintsLeft = practiceInfo?.hints ? practiceInfo.hints.allowed - practiceInfo.hints.used : 0;
  const canRetry = practiceInfo?.mode === 'development' && practiceInfo.retriesAllowed > 0;

  const handleLeave = () => onEnd();

  // A legacy (engine-off) conversation ended: it is not scored, and that is
  // said plainly — no "assessment failed", no level outcome, no score.
  if (legacyEnded) {
    return (
      <div className="py-12 text-center" data-testid="legacy-end">
        <h2 className="text-lg font-semibold text-gray-900">Conversation ended</h2>
        <p className="mt-2 text-sm text-gray-600">{LEGACY_END_NOTE}</p>
        <button
          type="button"
          onClick={handleLeave}
          className="mt-4 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
        >
          Done
        </button>
      </div>
    );
  }

  // Results view — replaces the transcript once the conversation is evaluated.
  if (showResults) {
    return (
      <div className="h-[calc(100vh-12rem)] flex flex-col">
        <div className="flex items-center justify-between pb-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">How that went</h2>
            <p className="text-sm text-gray-500">{scenario?.title ?? level?.displayName ?? persona.name}</p>
          </div>
          <div className="flex items-center gap-2">
            {canRetry && (
              <button
                type="button"
                onClick={handleRetry}
                className="px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg"
              >
                Try again
              </button>
            )}
            <button
              type="button"
              onClick={handleLeave}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
            >
              Done
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto py-6 space-y-4">
          {level && levelOutcome && <LevelOutcomeCard outcome={levelOutcome} levelName={level.displayName} />}
          {level && !levelOutcome && !evaluateError && result && (
            <p className="text-sm text-gray-600">This attempt was already recorded for {level.displayName}.</p>
          )}
          {examDomain && examLink?.state === 'linked' && examDefinition && (
            <ExamProgressPanel exam={examDefinition} progress={examLink.progress} />
          )}
          {examDomain && examLink?.state === 'refused' && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-4" role="alert">
              <p className="text-sm text-amber-900">{examLink.message}</p>
            </div>
          )}
          {examDomain && (
            <Link to={`/certifications/${examDomain}`} className="inline-block text-sm font-medium text-primary-600 hover:text-primary-700">
              Back to the exam
            </Link>
          )}
          {level && (
            <Link to="/npc/progress" className="inline-block text-sm font-medium text-primary-600 hover:text-primary-700">
              Back to NPC progress
            </Link>
          )}
          {finalizeNote && !evaluateError && (
            <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700">{finalizeNote}</p>
          )}
          {evaluateError ? (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-4" role="alert" data-testid="evaluate-error">
              <p className="text-sm text-amber-900">{evaluateError}</p>
              {retryFinalizeId && (
                <button
                  type="button"
                  onClick={() => void finalizer.retry(retryFinalizeId)}
                  disabled={evaluating}
                  className="mt-2 text-sm font-medium text-amber-900 underline disabled:opacity-50"
                >
                  {evaluating ? 'Trying again…' : 'Try again'}
                </button>
              )}
            </div>
          ) : result ? (
            <>
              <ConversationResults result={result} />
              {/* Explicit, owner-only, private by default; publishing is a separate per-item step. */}
              {result.status === 'evaluated' && result.sessionId && <AddResultToPortfolio sessionId={result.sessionId} />}
            </>
          ) : resultFailed ? (
            // The session is finalized; only reading its result failed. Say so
            // with Retry — never an endless loading line.
            <LoadErrorNotice
              error={resultError}
              onRetry={() => void refetchResult()}
              failedMessage="Your results could not be loaded."
              testIdPrefix="coaching-result"
            />
          ) : (
            <p className="text-sm text-gray-500 text-center">Loading your results…</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-12rem)] flex flex-col">
      {/* Session header */}
      <div className="flex items-center justify-between pb-4 border-b">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
            <span className="text-lg font-bold text-white">
              {persona.name[0]}
            </span>
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">{persona.name}</h2>
            <p className="text-sm text-gray-500">
              {scenario?.title ?? (level ? `Level: ${level.displayName} (pass at ${level.minOverallScore})` : 'Open conversation')}
              {practiceInfo?.mode === 'testing' && (
                <span className="ml-2 inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">Graded attempt</span>
              )}
              {sessionLanguage && (
                <span
                  data-testid="session-language"
                  className="ml-2 inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-700"
                >
                  {sessionLanguage.label}
                </span>
              )}
            </p>
          </div>
        </div>
        <button
          onClick={handleEndSession}
          disabled={evaluating}
          className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
        >
          {evaluating ? 'Evaluating…' : 'End Session'}
        </button>
      </div>

      {/* Messages area */}
      <div
        className="flex-1 overflow-y-auto py-4 space-y-4"
        onScroll={(e) => {
          // Auto-scroll only while the user is already near the bottom. Once they
          // scroll up to re-read something, streaming deltas must not drag the
          // view back down under them.
          const el = e.currentTarget;
          const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
          userPinnedRef.current = !nearBottom;
        }}
      >
        {evaluating && (
          <p className="rounded-lg bg-gray-50 p-3 text-center text-sm text-gray-700" role="status" data-testid="scoring-status">
            Scoring your conversation…
          </p>
        )}
        {legacyEndError && (
          <p className="rounded-lg bg-amber-50 p-3 text-center text-sm text-amber-900" role="alert">
            {legacyEndError}
          </p>
        )}
        {sessionError && (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <span className="text-red-500 text-xl">!</span>
            </div>
            <p className="text-gray-900 font-medium mb-2">{sessionError}</p>
            {sessionErrorIsCredits && <AddCreditsAction className="mb-3" />}
            <button
              onClick={onEnd}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Go back and try again
            </button>
          </div>
        )}
        {messages.map((message) => message.kind === 'hint' ? (
          <div key={message.id} className="flex justify-center">
            <div className="max-w-[85%] rounded-xl border border-amber-200 bg-amber-50 px-4 py-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Coach hint</p>
              <p className="text-sm text-amber-900 whitespace-pre-wrap">{message.text}</p>
            </div>
          </div>
        ) : (
          <div
            key={message.id}
            className={clsx(
              'flex',
              message.from === 'user' ? 'justify-end' : 'justify-start'
            )}
          >
            <div
              className={clsx(
                'max-w-[75%] px-4 py-3 rounded-2xl',
                message.from === 'user'
                  ? 'bg-primary-600 text-white rounded-br-md'
                  : 'bg-gray-100 text-gray-900 rounded-bl-md'
              )}
            >
              <p className="text-sm whitespace-pre-wrap">{message.text}</p>
              <p
                className={clsx(
                  'text-xs mt-1',
                  message.from === 'user' ? 'text-primary-200' : 'text-gray-400'
                )}
              >
                {new Date(message.createdAt).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
          </div>
        ))}

        {/* Live reply. Real deltas only — there is no artificial typing delay. */}
        {streamingText && (
          <div className="flex justify-start">
            <div className="max-w-[75%] px-4 py-3 rounded-2xl rounded-bl-md bg-gray-100 text-gray-900">
              <p className="text-sm whitespace-pre-wrap">
                {streamingText}
                <span className="inline-block w-1.5 h-4 ml-0.5 align-text-bottom bg-gray-400 animate-pulse" />
              </p>
            </div>
          </div>
        )}

        {/* Waiting indicator — only until the first delta actually arrives. */}
        {isCoachTyping && !streamingText && (
          <div className="flex justify-start">
            <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-md">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        {isCoachTyping && (
          <div className="flex justify-start">
            <button
              type="button"
              onClick={handleCancelReply}
              className="text-xs text-gray-500 underline hover:text-gray-700"
            >
              Stop generating
            </button>
          </div>
        )}

        {/* A failed turn persisted nothing. Say so, and give the text back. */}
        {sendError && (
          <div className="flex justify-start">
            <div className="max-w-[85%] px-4 py-3 rounded-xl bg-amber-50 border border-amber-200">
              <p className="text-sm text-amber-900">{sendError.message}</p>
              <div className="flex gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => { setInputValue(sendError.text); setSendError(null); }}
                  className="text-xs font-medium text-amber-900 underline"
                >
                  Restore my message
                </button>
                <button
                  type="button"
                  onClick={() => setSendError(null)}
                  className="text-xs text-amber-700"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="border-t pt-4">
        {/* Live Conversation — offered ONLY when the server says both the
            conversation engine and realtime voice are available. A mode whose
            first action returns an error is worse than no mode at all, so this
            button does not exist unless it will work. */}
        {realtimeVoiceAvailable && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2 mb-4">
            <button
              // Choosing a tab is UI state only. The attempt is created when
              // the user commits: first Send, or Start Live Conversation.
              onClick={() => setInputMode('text')}
              className={clsx(
                'flex-1 px-4 py-3 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2',
                inputMode === 'text' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700'
              )}
            >
              <ChatIcon className="w-4 h-4" />
              Written Chat
            </button>
            <button
              onClick={() => setInputMode('live')}
              disabled={liveBlockedReason !== null}
              title={liveBlockedReason ?? undefined}
              className={clsx(
                'flex-1 px-4 py-3 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2',
                inputMode === 'live' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700'
              )}
            >
              <MicIcon className="w-4 h-4" />
              Live Conversation
            </button>
            {/* Video — offered ONLY when the server reports the capability. */}
            {videoAvailable && (
              <button
                onClick={() => setInputMode('video')}
                disabled={videoBlockedReason !== null}
                title={videoBlockedReason ?? undefined}
                className={clsx(
                  'flex-1 px-4 py-3 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2',
                  inputMode === 'video' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700'
                )}
              >
                <CameraIcon className="w-4 h-4" />
                Video
              </button>
            )}
          </div>
        )}

        {inputMode === 'live' && realtimeVoiceAvailable && liveBlockedReason !== null && (
          <p className="mb-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-900" data-testid="live-testing-note">
            {liveBlockedReason}
          </p>
        )}
        {inputMode === 'live' && realtimeVoiceAvailable && liveBlockedReason === null && (
          <LiveConversationPanel
            wsUrl={(import.meta.env.VITE_WS_URL as string | undefined)
              ?? `${window.location.origin.replace(/^http/, 'ws')}/ws/realtime`}
            token={accessToken}
            // Every Start is a new spoken attempt: a spoken attempt is completed
            // server-side when its socket closes, so there is never an open one
            // to resume from this button.
            beginAttempt={() => createAttempt('realtime_audio')}
            personaName={persona.name}
            available={realtimeVoiceAvailable}
            onEnd={(endedId) => {
              // This attempt is over. The next conversation gets a new
              // canonical session rather than reopening a completed one —
              // and the ended one is scored now, once, with its result shown.
              setAttemptEnded(true);
              setInputMode('text');
              void finalizer.finish(endedId);
            }}
          />
        )}

        {inputMode === 'video' && videoAvailable && videoBlockedReason !== null && (
          <p className="mb-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-900" data-testid="video-language-note">
            {videoBlockedReason}
          </p>
        )}
        {inputMode === 'video' && videoAvailable && videoBlockedReason === null && (
          <VideoPracticePanel
            wsUrl={(import.meta.env.VITE_WS_URL as string | undefined)
              ?? `${window.location.origin.replace(/^http/, 'ws')}/ws/realtime`}
            token={accessToken}
            // The modality is the commitment: frozen into the binding as video.
            beginAttempt={() => createAttempt('realtime_audio_video')}
            personaName={persona.name}
            available={videoAvailable}
            mode={practiceMode === 'testing' && canTest ? 'testing' : 'development'}
            onEnd={(endedId) => {
              setAttemptEnded(true);
              setInputMode('text');
              void finalizer.finish(endedId);
            }}
          />
        )}

        {/* The conversation language, chosen before the attempt starts. It
            applies to Written Chat, Start Live Conversation and Video alike. */}
        {!sessionId && languageLearning && (
          <p className="mb-3 text-center text-xs text-gray-600" data-testid="language-learning-note">
            Practising in {languageLearning.request.language} at your self-reported level {languageLearning.request.proficiency}
            {practiceMode === 'testing' ? ' — graded: the conversation stays in this language, with no help in your own.' : '.'}
          </p>
        )}
        {!sessionId && songPractice && (
          <p className="mb-3 text-center text-xs text-gray-600" data-testid="song-practice-note">
            Speak-along practice in {practiceLanguageOption(songPractice.language)?.label ?? songPractice.language}: feedback on
            your use of the language. Pronunciation and rhythm are not measured.
          </p>
        )}
        {!sessionId && !pendingRetryOf && !languageLearning && !songPractice && (
          <PracticeLanguagePicker
            language={practiceLanguage}
            onLanguageChange={setPracticeLanguage}
            mode={practiceMode === 'testing' && canTest ? 'testing' : 'development'}
            languageAssessment={languageAssessment}
            onLanguageAssessmentChange={setLanguageAssessment}
          />
        )}

        {/* Development or Testing, chosen before the attempt starts — unless the
            link fixed it (an exam is always graded; a level is not chosen). */}
        {lockMode && !sessionId && practiceMode === 'testing' && (
          <p className="mb-3 text-center text-xs text-gray-600" data-testid="graded-attempt-note">
            Graded attempt{examDomain ? ' for your certification exam' : ''}: no hints or retries.
          </p>
        )}
        {!sessionId && !pendingRetryOf && !lockMode && (
          <div className="flex items-center justify-center gap-2 mb-3" role="radiogroup" aria-label="Practice mode">
            {(['development', 'testing'] as const).map((m) => (
              <button
                key={m}
                role="radio"
                aria-checked={practiceMode === m}
                // Withheld up front — before consent, camera or Start — when no
                // authored rubric can grade this conversation in the chosen
                // modality; the server would refuse it (never a generic rubric).
                disabled={m === 'testing' && (!canTest || testingUnavailableIn(inputMode))}
                title={m === 'testing'
                  ? !canTest
                    ? engineOff ? GRADED_CONVERSATION_UNAVAILABLE_NOTE : (interviewPrep ? 'Testing needs an authored interview rubric' : languageLearning ? 'Testing needs an authored rubric for this conversation' : authoredTestingModalities ? 'Testing needs an authored rubric for this scenario' : 'Testing needs an authored scenario')
                    : testingUnavailableIn(inputMode)
                      ? (inputMode === 'video' ? VIDEO_TESTING_UNAVAILABLE_NOTE : inputMode === 'live' ? LIVE_TESTING_UNAVAILABLE_NOTE : TEXT_TESTING_UNAVAILABLE_NOTE)
                      : undefined
                  : undefined}
                onClick={() => setPracticeMode(m)}
                className={clsx(
                  'px-3 py-1.5 text-xs font-medium rounded-full transition-colors disabled:opacity-40',
                  practiceMode === m ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                )}
              >
                {m === 'development' ? 'Practice (coaching on)' : 'Test (graded)'}
              </button>
            ))}
          </div>
        )}

        {/* Text Input Mode */}
        {inputMode === 'text' && (
          <>
            {practiceInfo?.mode === 'development' && practiceInfo.hints && sessionId && !attemptEnded && (
              <div className="flex items-center justify-end gap-2 mb-2">
                {hintError && <span className="text-xs text-amber-700">{hintError}</span>}
                <button
                  type="button"
                  onClick={handleHint}
                  disabled={hintBusy || hintsLeft <= 0}
                  className="px-3 py-1 text-xs font-medium rounded-lg bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                >
                  {hintBusy ? 'Thinking…' : `Hint (${hintsLeft} left)`}
                </button>
              </div>
            )}
            {textBlockedReason !== null && (
              <p className="mb-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-900" data-testid="text-testing-note">
                {textBlockedReason}
              </p>
            )}
            <div className="flex gap-3">
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your response... (Enter to send)"
                className="flex-1 resize-none rounded-xl border border-gray-300 px-4 py-3 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none"
                rows={2}
              />
              {/* Held only until the server has stated its transport — a brief,
                  honest wait rather than a guess that could pick the wrong path. */}
              <button
                onClick={handleSend}
                disabled={!inputValue.trim() || transport === 'resolving' || textBlockedReason !== null}
                className={clsx(
                  'px-6 rounded-xl font-medium transition-colors',
                  inputValue.trim() && transport !== 'resolving' && textBlockedReason === null
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                )}
              >
                <SendIcon className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">
              Press Enter to send • Shift+Enter for new line
            </p>
          </>
        )}

      </div>
    </div>
  );
}

function SendIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
      />
    </svg>
  );
}

function ChatIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
      />
    </svg>
  );
}

function CameraIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
    </svg>
  );
}

function MicIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
      />
    </svg>
  );
}
